import { useCallback, useMemo, useState } from "react";
import { ActivityIndicator, Text, View } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import OnboardingLayout, {
  ContinueButton,
  ScreenSubtitle,
  ScreenTitle,
} from "../components/OnboardingLayout";
import { EMBER } from "../theme";
import { useOnboarding } from "../OnboardingContext";
import type { RootStackParamList } from "../../App";
import { useCarePersons } from "@src/packages/patients/hooks/useCarePersons";
import {
  useUpdatePatient,
  usePatient,
} from "@src/packages/patients/hooks/usePatient";
import { useTranslations } from "@src/packages/i18n";
import { resolvePlannedModality } from "@src/packages/homepage/plan/resolvePlannedModality";

type Patient = NonNullable<ReturnType<typeof usePatient>["data"]>;
type FertilityIntent = NonNullable<Patient["fertilityIntent"]>;
type FertilityIntentGoal = FertilityIntent["goal"];

type UpdatePatientPayload = Parameters<
  ReturnType<typeof useUpdatePatient>["mutate"]
>[0];
type FertilityIntentPatch = NonNullable<
  UpdatePatientPayload["fertilityIntent"]
>;
type PartnerFertilityIntentPatch = Omit<
  FertilityIntentPatch,
  "periodRegularity"
> & {
  periodRegularity?: FertilityIntentPatch["periodRegularity"];
};

/**
 * Human-readable label for the inviter's journey, used in the confirmation
 * sentence. Mirrors the (goal, modality) buckets in resolveJourneyKey().
 */
function describeJourney(
  goal: FertilityIntentGoal | null | undefined,
  modality: "ivf" | "iui" | null,
): string {
  if (goal === "pregnancy") {
    if (modality === "iui") return "IUI";
    if (modality === "ivf") return "IVF";
    return "fertility care";
  }
  if (goal === "egg-preserve" || goal === "egg-freezing") return "egg-freezing";
  if (goal === "sperm-preserve") return "sperm-freezing";
  if (goal === "embryo-preserve") return "embryo-freezing";
  return "fertility";
}

export default function PartnerJourneyConfirmScreen() {
  const t = useTranslations();
  const rootNav =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { data, clearData } = useOnboarding();
  const { partnerPatient, partnerName, hasLinkedPartner } = useCarePersons();
  const updatePatient = useUpdatePatient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inviterName =
    partnerName ?? t("onboarding.partner_confirm.default_partner_name");

  const inviterIntent = partnerPatient?.fertilityIntent ?? null;
  const inviterGoal = inviterIntent?.goal ?? null;
  const inviterModality = resolvePlannedModality(
    inviterIntent?.plannedModalities,
  );

  const journeyLabel = useMemo(
    () => describeJourney(inviterGoal, inviterModality),
    [inviterGoal, inviterModality],
  );

  const goHome = useCallback(() => {
    clearData();
    rootNav.reset({ index: 0, routes: [{ name: "Home" }] });
  }, [clearData, rootNav]);

  const handleConfirm = useCallback(async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      // Mirror the inviter's fertilityIntent onto the partner so the journey
      // resolver picks the matching `*-with-partner-male` / `*-with-partner`
      // variant. The partner flow does not ask about menstrual regularity, so
      // omit only that field while preserving the inviter's journey data.
      const fertilityIntent: PartnerFertilityIntentPatch | null = inviterIntent
        ? {
            ...(data.periodRegularity
              ? { periodRegularity: data.periodRegularity }
              : {}),
            ...(inviterGoal ? { goal: inviterGoal } : {}),
            ...(inviterIntent.primaryIntent
              ? { primaryIntent: inviterIntent.primaryIntent }
              : {}),
            ...(inviterIntent.stage ? { stage: inviterIntent.stage } : {}),
            ...(inviterIntent.plannedModalities
              ? { plannedModalities: inviterIntent.plannedModalities }
              : {}),
          }
        : null;

      await new Promise<void>((resolve, reject) => {
        // dashboard-api#1238 makes periodRegularity optional. Regenerate the
        // pinned client contract after that API change lands.
        const partnerPayload = {
          partnerChoice: "with-partner" as const,
          ...(fertilityIntent ? { fertilityIntent } : {}),
        } as UpdatePatientPayload;
        updatePatient.mutate(partnerPayload, {
          onSuccess: () => resolve(),
          onError: (err) => reject(err),
        });
      });

      goHome();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("onboarding.partner_confirm.save_error"),
      );
      setIsSubmitting(false);
    }
  }, [
    data.periodRegularity,
    inviterGoal,
    inviterIntent,
    updatePatient,
    goHome,
    t,
  ]);

  // If we haven't yet seen the partner appear on the self record (accept may
  // still be propagating), show a loading state instead of locking the user.
  if (!hasLinkedPartner) {
    return (
      <OnboardingLayout>
        <View style={{ alignItems: "center", paddingTop: 60 }}>
          <ActivityIndicator color={EMBER.colors.warm} />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              color: EMBER.colors.textLight,
              marginTop: 16,
              textAlign: "center",
            }}
          >
            {t("onboarding.partner_confirm.linking_accounts")}
          </Text>
        </View>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout>
      <ScreenTitle>
        {t("onboarding.partner_confirm.title", {
          params: { name: inviterName },
        })}
      </ScreenTitle>
      <ScreenSubtitle>
        {t("onboarding.partner_confirm.subtitle", {
          params: { name: inviterName, journey: journeyLabel },
        })}
      </ScreenSubtitle>

      <View
        style={{
          backgroundColor: EMBER.colors.surface,
          borderRadius: 12,
          borderWidth: 1,
          borderColor: EMBER.colors.border,
          padding: 20,
          marginTop: 8,
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            fontWeight: "600",
            letterSpacing: 1.2,
            color: EMBER.colors.textFaint,
            textTransform: "uppercase",
            marginBottom: 8,
          }}
        >
          {t("onboarding.partner_confirm.their_journey")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 18,
            color: EMBER.colors.text,
            marginBottom: 4,
          }}
        >
          {inviterName}
        </Text>
        <Text
          className="font-dm-sans"
          style={{ fontSize: 14, color: EMBER.colors.textMid }}
        >
          {journeyLabel.charAt(0).toUpperCase() + journeyLabel.slice(1)}
        </Text>
      </View>

      {error && (
        <Text
          testID="partner-journey-confirm-error"
          className="font-dm-sans"
          style={{
            fontSize: 13,
            color: "#ef4444",
            textAlign: "center",
            marginTop: 12,
          }}
        >
          {error}
        </Text>
      )}

      <ContinueButton
        title={
          isSubmitting
            ? t("onboarding.partner_confirm.cta_saving")
            : t("onboarding.partner_confirm.cta_save")
        }
        onPress={handleConfirm}
        disabled={isSubmitting}
      />
    </OnboardingLayout>
  );
}
